import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileSpreadsheet, FileText, Printer, Play } from "lucide-react";
import { toast } from "sonner";
import api, { formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface } from "@/components/app/Primitives";

const today = new Date();
const iso = (d) => d.toISOString().slice(0, 10);
const monthStart = iso(new Date(today.getFullYear(), today.getMonth(), 1));
const monthEnd = iso(new Date(today.getFullYear(), today.getMonth() + 1, 0));

const fmt = (type, v) => {
  if (v === null || v === undefined || v === "") return "-";
  if (type === "money") return Number(v).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (type === "int") return Math.round(Number(v)).toLocaleString("en-PH");
  if (type === "hours") return Number(v).toLocaleString("en-PH", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return String(v);
};
const isNum = (t) => t === "money" || t === "int" || t === "hours";

async function blobError(e) {
  try {
    const t = await e.response.data.text();
    return formatErrorDetail(JSON.parse(t).detail) || "Report failed";
  } catch {
    return formatErrorDetail(e.response?.data?.detail) || "Report failed";
  }
}

function saveBlob(r, fallback) {
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  const m = /filename="?([^";]+)"?/.exec(r.headers?.["content-disposition"] || "");
  a.href = url;
  a.download = m ? m[1] : fallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ReportsCenter() {
  const [cat, setCat] = useState(null);
  const [key, setKey] = useState("payroll_register");
  const [p, setP] = useState({ period_start: monthStart, period_end: monthEnd, compare_start: "", compare_end: "", year: String(today.getFullYear()), run_id: "", department: "", status: "open", include_drafts: false });
  const [custom, setCustom] = useState({ source: "payroll", columns: ["employee_name", "department", "gross_pay", "net_pay"], group_by: "", title: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/catalog").then((r) => setCat(r.data)).catch((e) => toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not load the reports"));
  }, []);

  const def = useMemo(() => cat?.reports.find((r) => r.key === key), [cat, key]);
  const groups = useMemo(() => {
    const g = {};
    (cat?.reports || []).forEach((r) => { (g[r.group] = g[r.group] || []).push(r); });
    return g;
  }, [cat]);
  if (!cat || !def) return <div className="p-6 text-sm text-muted-foreground">Loading reports...</div>;

  const src = cat.sources[custom.source];
  const params = def.params || [];
  const set = (k, v) => setP({ ...p, [k]: v });

  const query = () => {
    const q = {};
    if (params.includes("run_or_period") && p.run_id) q.run_id = p.run_id;
    else if (params.includes("period") || params.includes("run_or_period") || (params.includes("period_optional") && p.period_start)) { q.period_start = p.period_start; q.period_end = p.period_end; }
    if (params.includes("compare")) { q.compare_start = p.compare_start; q.compare_end = p.compare_end; }
    if (params.includes("year")) q.year = p.year;
    if (params.includes("status")) q.status = p.status;
    if (p.department && !params.includes("status")) q.department = p.department;
    if (p.include_drafts && (params.includes("period") || params.includes("run_or_period"))) q.include_drafts = "true";
    return q;
  };
  const customBody = (format) => ({ source: custom.source, columns: custom.columns, group_by: custom.group_by || null, title: custom.title, period_start: p.period_start, period_end: p.period_end, department: p.department || null, include_drafts: p.include_drafts, format });

  const run = async () => {
    setBusy(true);
    try {
      const r = key === "custom" ? await api.post("/reports/custom", customBody("json")) : await api.get(`/reports/${key}`, { params: query() });
      setRes(r.data);
      if (!r.data.rows.length) toast.message("No records for those choices");
    } catch (e) {
      setRes(null);
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Report failed");
    } finally { setBusy(false); }
  };
  const download = async (format) => {
    setBusy(true);
    try {
      const r = key === "custom" ? await api.post("/reports/custom", customBody(format), { responseType: "blob" }) : await api.get(`/reports/${key}`, { params: { ...query(), format }, responseType: "blob" });
      saveBlob(r, `report.${format}`);
    } catch (e) { toast.error(await blobError(e)); } finally { setBusy(false); }
  };
  const toggleCol = (k) => setCustom({ ...custom, columns: custom.columns.includes(k) ? custom.columns.filter((x) => x !== k) : [...custom.columns, k] });

  const Field = ({ label, children }) => (<label className="block text-xs"><span className="text-muted-foreground">{label}</span>{children}</label>);
  const inp = "mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm";

  return (
    <div>
      <style>{`@media print { body * { visibility: hidden; } #report-print-area, #report-print-area * { visibility: visible; } #report-print-area { position: absolute; left: 0; top: 0; width: 100%; } .no-print { display: none !important; } }`}</style>
      <PageHeader title="Reports" subtitle="Payroll, government, tax, pay-component, time and people reports for the active company. Export to PDF, Excel or CSV, or print." />
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <div className="space-y-3 no-print" data-testid="report-list">
          {Object.entries(groups).map(([g, list]) => (
            <Surface key={g} className="p-2">
              <div className="px-2 py-1 text-[11px] font-semibold uppercase text-muted-foreground">{g}</div>
              {list.map((r) => (
                <button key={r.key} onClick={() => { setKey(r.key); setRes(null); }} className={`block w-full text-left px-2 py-1.5 rounded text-sm ${r.key === key ? "bg-primary text-white" : "hover:bg-secondary"}`} data-testid={`report-${r.key}`}>{r.title}</button>
              ))}
            </Surface>
          ))}
        </div>
        <div className="space-y-3">
          <Surface className="p-4 no-print">
            <div className="font-display font-semibold">{def.title}</div>
            <div className="text-xs text-muted-foreground mt-1">{def.about}</div>
            {(def.links || []).map((l) => (<div key={l.label} className="text-xs mt-1"><Link className="text-primary underline" to={l.to}>{l.label}</Link></div>))}
            {def.runnable ? (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                {params.includes("run_or_period") && (
                  <Field label="One payroll run (optional)">
                    <select className={inp} value={p.run_id} onChange={(e) => set("run_id", e.target.value)} data-testid="param-run">
                      <option value="">All runs in the period</option>
                      {cat.runs.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                  </Field>
                )}
                {(params.includes("period") || params.includes("run_or_period") || params.includes("period_optional") || key === "custom") && !(params.includes("run_or_period") && p.run_id) && (<>
                  <Field label="Period from"><input type="date" className={inp} value={p.period_start} onChange={(e) => set("period_start", e.target.value)} data-testid="param-from" /></Field>
                  <Field label="Period to"><input type="date" className={inp} value={p.period_end} onChange={(e) => set("period_end", e.target.value)} data-testid="param-to" /></Field>
                </>)}
                {params.includes("compare") && (<>
                  <Field label="Compare with, from"><input type="date" className={inp} value={p.compare_start} onChange={(e) => set("compare_start", e.target.value)} /></Field>
                  <Field label="Compare with, to"><input type="date" className={inp} value={p.compare_end} onChange={(e) => set("compare_end", e.target.value)} /></Field>
                </>)}
                {params.includes("year") && <Field label="Year"><input type="number" className={inp} value={p.year} onChange={(e) => set("year", e.target.value)} data-testid="param-year" /></Field>}
                {params.includes("status") && (
                  <Field label="Loans">
                    <select className={inp} value={p.status} onChange={(e) => set("status", e.target.value)}>
                      <option value="open">Open (approved, active, paused)</option><option value="all">All</option><option value="paid">Paid</option><option value="cancelled">Cancelled</option>
                    </select>
                  </Field>
                )}
                {!params.includes("status") && (
                  <Field label="Department">
                    <select className={inp} value={p.department} onChange={(e) => set("department", e.target.value)}>
                      <option value="">All departments</option>{cat.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </Field>
                )}
                {(params.includes("period") || params.includes("run_or_period") || key === "custom") && (
                  <label className="flex items-end gap-2 text-xs pb-2"><input type="checkbox" checked={p.include_drafts} onChange={(e) => set("include_drafts", e.target.checked)} /> Include runs still in draft</label>
                )}
              </div>
            ) : null}
            {key === "custom" && (
              <div className="mt-3 space-y-2" data-testid="custom-builder">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Field label="Source">
                    <select className={inp} value={custom.source} onChange={(e) => setCustom({ ...custom, source: e.target.value, columns: cat.sources[e.target.value].columns.slice(0, 4).map((c) => c.key), group_by: "" })} data-testid="custom-source">
                      {Object.entries(cat.sources).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Group by (adds up the number columns)">
                    <select className={inp} value={custom.group_by} onChange={(e) => setCustom({ ...custom, group_by: e.target.value })}>
                      <option value="">No grouping, one row per record</option>{src.group.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </Field>
                  <Field label="Title (optional)"><input className={inp} value={custom.title} onChange={(e) => setCustom({ ...custom, title: e.target.value })} /></Field>
                </div>
                <div className="text-xs text-muted-foreground">Columns</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {src.columns.map((c) => (<label key={c.key} className="text-xs flex items-center gap-1"><input type="checkbox" checked={custom.columns.includes(c.key)} onChange={() => toggleCol(c.key)} />{c.label}</label>))}
                </div>
                <div className="text-[11px] text-muted-foreground">Government ids, bank accounts and birth dates are not offered here. The SSS, PhilHealth, Pag-IBIG and Withholding Tax reports carry the ids they need.</div>
              </div>
            )}
            {def.runnable && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={run} disabled={busy} className="h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-2 disabled:opacity-50" data-testid="report-run"><Play className="size-4" /> Run report</button>
                {res && (<>
                  <button onClick={() => download("pdf")} disabled={busy} className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-2" data-testid="export-pdf"><FileText className="size-4" /> PDF</button>
                  <button onClick={() => download("xlsx")} disabled={busy} className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-2" data-testid="export-xlsx"><FileSpreadsheet className="size-4" /> Excel</button>
                  <button onClick={() => download("csv")} disabled={busy} className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-2" data-testid="export-csv"><Download className="size-4" /> CSV</button>
                  <button onClick={() => window.print()} className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-2" data-testid="report-print"><Printer className="size-4" /> Print</button>
                </>)}
              </div>
            )}
          </Surface>
          {res && (
            <Surface className="p-3 overflow-x-auto" >
              <div id="report-print-area" data-testid="report-result">
                <div className="font-display font-semibold">{res.title}</div>
                <div className="text-xs text-muted-foreground mb-2">{res.subtitle} - {res.rows.length} row(s){res.meta?.truncated ? " (first 20,000 shown)" : ""}</div>
                <table className="w-full text-xs">
                  <thead><tr>{res.columns.map((c) => <th key={c.key} className={`px-2 py-1 border-b border-border font-semibold ${isNum(c.type) ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr></thead>
                  <tbody>
                    {res.rows.map((r, i) => (<tr key={i} className="border-b border-border/50">{res.columns.map((c) => <td key={c.key} className={`px-2 py-1 ${isNum(c.type) ? "text-right font-mono" : ""}`}>{fmt(c.type, r[c.key])}</td>)}</tr>))}
                    {!res.rows.length && <tr><td colSpan={res.columns.length} className="px-2 py-6 text-center text-muted-foreground">No records for those choices.</td></tr>}
                  </tbody>
                  {Object.keys(res.totals || {}).length > 0 && (
                    <tfoot><tr className="font-bold bg-secondary/60">{res.columns.map((c, i) => <td key={c.key} className={`px-2 py-1 ${isNum(c.type) ? "text-right font-mono" : ""}`}>{i === 0 ? "TOTAL" : (c.key in res.totals ? fmt(c.type, res.totals[c.key]) : "")}</td>)}</tr></tfoot>
                  )}
                </table>
              </div>
            </Surface>
          )}
        </div>
      </div>
    </div>
  );
}
